// Session context. Until WorkOS browser auth lands (C1.3 follow-up), a dev session
// (super_tenant_admin) drives RBAC + the dev session header. PLAN-C4.0 task-2.
import { createContext, useContext, useEffect, type ReactNode } from "react";
import type { SympllexSession } from "@sympllex/auth";
import { setApiSession } from "./api";

export const DEV_SESSION: SympllexSession = {
  user: {
    id: "00000000-0000-0000-0000-000000000001",
    workosUserId: "wos-dev",
    email: "admin@sympllex.dev",
    fullName: "Dev Admin",
  },
  activeTenantId: "00000000-0000-0000-0000-0000000000aa",
  availableTenantIds: ["00000000-0000-0000-0000-0000000000aa"],
  roleCodes: ["super_tenant_admin"],
  expiresAt: 4_102_444_800, // year 2100
};

// The booted session (set by main.tsx after /auth/me, or DEV_SESSION in dev). The provider + the
// router context both read it so RBAC gates + the cockpit see the real signed-in user (C1.3 F1d).
let bootedSession: SympllexSession = DEV_SESSION;
export function setBootedSession(s: SympllexSession): void { bootedSession = s; setApiSession(s); }
export function getBootedSession(): SympllexSession { return bootedSession; }

const SessionCtx = createContext<SympllexSession | null>(null);

export function SessionProvider({ children, session }: { children: ReactNode; session?: SympllexSession }) {
  const active = session ?? getBootedSession();
  useEffect(() => { setApiSession(active); }, [active]);
  return <SessionCtx.Provider value={active}>{children}</SessionCtx.Provider>;
}

export function useSession(): SympllexSession {
  const s = useContext(SessionCtx);
  if (!s) throw new Error("useSession must be used within <SessionProvider>");
  return s;
}
